import { IProductOnboardingContract } from "@features/onboarding/ui/productOnboarding";
import { IRouterContract } from "./../../shared/router/index";
import { routePathNameChanged } from "@features/app/model";
import { ModelContract, ModuleDescription } from "@features/shared/module-description";
import { createEffect, createEvent, createStore, sample } from "effector";
import { returnToProfileOrHomePageByLocalStorageFlag } from "@features/helpers";
import { IUpsellContract } from "../model";
import { reset } from "patronum";
import { IAppRouteUnitsContract } from "@features/app/route-units";
import { IAmplitudeFlagsContract } from "@features/amplitude-flags/model";
import { IPlatformContextContract } from "@features/launch-context/model";
import { getCreativeFamily } from "@features/upsell/creative";

const skipButtonClicked = createEvent();
const bundleUpsellClosed = createEvent();
const upsellSaleModalOpened = createEvent();
const aiToolsPaymentStatusModalClosed = createEvent();
const failedUpsellModalOpened = createEvent();
const failedUpsellModalClosed = createEvent();
const packAccessRedirectTriggered = createEvent();

const $isFailedUpsellModalOpen = createStore(false)
    .on(failedUpsellModalOpened, () => true)
    .on(failedUpsellModalClosed, () => false)
    .on(aiToolsPaymentStatusModalClosed, () => false);
const $failedAttemptCount = createStore(0).on(failedUpsellModalClosed, (s) => s + 1);

const deleteFromProfileFlagFx = createEffect(() => {
    if (typeof window !== "undefined") localStorage.removeItem("prevUrl");
});

const contract = new ModelContract(
    {
        $isFailedUpsellModalOpen
    },
    {
        failedUpsellModalOpened,
        failedUpsellModalClosed,
        skipButtonClicked,
        bundleUpsellClosed,
        aiToolsPaymentStatusModalClosed,
        upsellSaleModalOpened,
        packAccessRedirectTriggered
    },
    {},
    {}
);

export type IUpsellUIContract = typeof contract;

export class UpsellUIModule extends ModuleDescription<IUpsellUIContract> {
    constructor(
        public routerModule: IRouterContract,
        public productOnboardingModule: IProductOnboardingContract,
        public upsellModule: IUpsellContract,
        public routesModule: IAppRouteUnitsContract,
        public amplitudeFlagsModule: IAmplitudeFlagsContract,
        public platformContextModule: IPlatformContextContract
    ) {
        super();
    }

    getDefaultContract() {
        return contract;
    }

    run(): void {
        // skipButtonClicked → if NOT first login and NOT from profile, redirect to pack page
        sample({
            clock: skipButtonClicked,
            source: {
                currentUpsellId: this.upsellModule.stores.$currentUpsellId,
                fromProfile: this.upsellModule.stores.$fromProfile,
                firstLoginFlag: this.upsellModule.stores.$firstLoginFlag
            },
            filter: (s) => !s.fromProfile && !s.firstLoginFlag && s.currentUpsellId !== null,
            target: packAccessRedirectTriggered
        });

        // skipButtonClicked → if first login and next non-sale upsell exists, redirect to it
        sample({
            clock: skipButtonClicked,
            source: {
                upsells: this.upsellModule.stores.$upsells,
                currentUpsellId: this.upsellModule.stores.$currentUpsellId,
                fromProfile: this.upsellModule.stores.$fromProfile,
                firstLoginFlag: this.upsellModule.stores.$firstLoginFlag
            },
            filter: (s) => {
                if (!s.currentUpsellId || s.fromProfile || !s.firstLoginFlag) return false;
                const currentIndex = s.upsells.findIndex((u) => u.upsellId === s.currentUpsellId);
                if (currentIndex === -1) return false;
                const nextIndex = currentIndex + 1;
                return nextIndex < s.upsells.length && !s.upsells[nextIndex].upsellId.includes("sale");
            },
            fn: (s) => {
                const currentIndex = s.upsells.findIndex((u) => u.upsellId === s.currentUpsellId);
                const nextUpsellId = s.upsells[currentIndex + 1].upsellId;
                return `/upsell/${nextUpsellId}`;
            },
            target: this.routerModule.events.routerReplace
        });

        // skipButtonClicked → if first login and next is sale upsell, open sale modal
        sample({
            clock: skipButtonClicked,
            source: {
                upsells: this.upsellModule.stores.$upsells,
                currentUpsellId: this.upsellModule.stores.$currentUpsellId,
                fromProfile: this.upsellModule.stores.$fromProfile,
                firstLoginFlag: this.upsellModule.stores.$firstLoginFlag
            },
            filter: (s) => {
                if (!s.currentUpsellId || s.fromProfile || !s.firstLoginFlag) return false;
                const currentIndex = s.upsells.findIndex((u) => u.upsellId === s.currentUpsellId);
                if (currentIndex === -1) return false;
                const nextIndex = currentIndex + 1;
                return nextIndex < s.upsells.length && s.upsells[nextIndex].upsellId.includes("sale");
            },
            target: upsellSaleModalOpened
        });

        // skipButtonClicked → if first login and last upsell, go home
        sample({
            clock: skipButtonClicked,
            source: {
                upsells: this.upsellModule.stores.$upsells,
                currentUpsellId: this.upsellModule.stores.$currentUpsellId,
                fromProfile: this.upsellModule.stores.$fromProfile,
                firstLoginFlag: this.upsellModule.stores.$firstLoginFlag,
                isMetaBrowser: this.platformContextModule.stores.$isMetaBrowser
            },
            filter: (s) => {
                if (!s.currentUpsellId || s.fromProfile || !s.firstLoginFlag || s.isMetaBrowser)
                    return false;
                const currentIndex = s.upsells.findIndex((u) => u.upsellId === s.currentUpsellId);

                if (currentIndex + 1 < s.upsells.length) return false;

                return true;
            },
            fn: () => returnToProfileOrHomePageByLocalStorageFlag(),
            target: [
                this.routerModule.events.routerReplace,
                this.productOnboardingModule.events.finishUpsellAndStartProductOnboarding,
                this.upsellModule.events.claudeFunnelMetadataAppendRequested,
                this.upsellModule.events.firstLoginFlagReset
            ]
        });

        sample({
            clock: skipButtonClicked,
            source: {
                upsells: this.upsellModule.stores.$upsells,
                currentUpsellId: this.upsellModule.stores.$currentUpsellId,
                fromProfile: this.upsellModule.stores.$fromProfile,
                firstLoginFlag: this.upsellModule.stores.$firstLoginFlag,
                isMetaBrowser: this.platformContextModule.stores.$isMetaBrowser
            },
            filter: (s) => {
                if (!s.currentUpsellId || s.fromProfile || !s.firstLoginFlag || !s.isMetaBrowser)
                    return false;
                const currentIndex = s.upsells.findIndex((u) => u.upsellId === s.currentUpsellId);

                if (currentIndex + 1 < s.upsells.length) return false;

                return true;
            },
            fn: () => {
                localStorage.removeItem("first_login");
                return "/change-browser";
            },
            target: [
                this.routerModule.events.routerReplace,
                this.upsellModule.events.claudeFunnelMetadataAppendRequested,
                this.upsellModule.events.firstLoginFlagReset
            ]
        });

        // skipButtonClicked → if from profile, return to profile or home
        sample({
            clock: skipButtonClicked,
            source: {
                fromProfileFlag: this.upsellModule.stores.$fromProfile
            },
            filter: (s) => Boolean(s.fromProfileFlag),
            fn: () => returnToProfileOrHomePageByLocalStorageFlag(),
            target: [this.routerModule.events.routerReplace, deleteFromProfileFlagFx]
        });

        sample({
            clock: packAccessRedirectTriggered,
            source: {
                currentUpsellId: this.upsellModule.stores.$currentUpsellId
            },
            filter: (s) => s.currentUpsellId !== null,
            fn: (s) => {
                // The drama / film products are downloads handed out on the
                // profile, their bundle included.
                if (getCreativeFamily(s.currentUpsellId)) {
                    return "/profile";
                }

                // Grants every pack at once — land on the library root where all
                // of them are visible.
                if (s.currentUpsellId!.includes("complete_bundle")) {
                    return "/prompts-library";
                }

                if (s.currentUpsellId!.includes("claude_work_bundle")) {
                    return "/prompts-library/claude_pack";
                }

                if (
                    s.currentUpsellId!.includes("30k_prompts") ||
                    s.currentUpsellId!.includes("30k_prompts_complete_pack")
                ) {
                    return "/prompts-library?pack=ultimate_ai";
                }

                if (s.currentUpsellId!.includes("600_prompts")) {
                    return "/prompts-library?pack=personal_boost";
                }

                if (s.currentUpsellId!.includes("ai_tools")) {
                    return "/assistant";
                }

                return "/home";
            },
            target: this.routerModule.events.routerReplace
        });

        // successUpsellRegistrationCompleted → if NOT first login and NOT from profile, redirect to pack page
        // NEW FLOW
        sample({
            clock: [
                this.upsellModule.effects.registerEcommpayUpsellFx.doneData,
                this.upsellModule.effects.registerTruegateUpsellFx.doneData,
                this.upsellModule.events.upsellChargeSucceeded,
                this.upsellModule.events.upsellUpiPaymentSucceeded,
                this.upsellModule.effects.chargeAppleUpsellFx.doneData
            ],
            source: {
                currentUpsellId: this.upsellModule.stores.$currentUpsellId,
                fromProfile: this.upsellModule.stores.$fromProfile,
                firstLoginFlag: this.upsellModule.stores.$firstLoginFlag,
                isAiToolsRoute: this.routesModule.stores.aiTools.$isActive,
                isAssistantRoute: this.routesModule.stores.assistant.$isActive,
                isLessonRoute: this.routesModule.stores.lesson.$isActive
            },
            filter: (s) =>
                !s.fromProfile &&
                !s.firstLoginFlag &&
                s.currentUpsellId !== null &&
                !s.isAiToolsRoute &&
                !s.isAssistantRoute &&
                !s.isLessonRoute,
            target: packAccessRedirectTriggered
        });

        // successUpsellRegistrationCompleted → if first login and next non-sale upsell exists, redirect to it
        // NEW FLOW
        sample({
            clock: [
                this.upsellModule.effects.registerEcommpayUpsellFx.doneData,
                this.upsellModule.effects.registerTruegateUpsellFx.doneData,
                this.upsellModule.events.upsellChargeSucceeded,
                this.upsellModule.events.upsellUpiPaymentSucceeded,
                this.upsellModule.effects.chargeAppleUpsellFx.doneData
            ],
            source: {
                upsells: this.upsellModule.stores.$upsells,
                currentUpsellId: this.upsellModule.stores.$currentUpsellId,
                fromProfile: this.upsellModule.stores.$fromProfile,
                firstLoginFlag: this.upsellModule.stores.$firstLoginFlag
            },
            filter: (s) => {
                if (s.fromProfile || !s.currentUpsellId || !s.firstLoginFlag) return false;
                const currentIndex = s.upsells.findIndex((u) => u.upsellId === s.currentUpsellId);
                if (currentIndex === -1) return false;
                const nextNonSaleIndex = s.upsells.findIndex(
                    (u, idx) => idx > currentIndex && !u.upsellId.includes("sale")
                );
                return nextNonSaleIndex !== -1;
            },
            fn: (s) => {
                const currentIndex = s.upsells.findIndex((u) => u.upsellId === s.currentUpsellId);
                const nextNonSaleIndex = s.upsells.findIndex(
                    (u, idx) => idx > currentIndex && !u.upsellId.includes("sale")
                );
                return `/upsell/${s.upsells[nextNonSaleIndex].upsellId}`;
            },
            target: this.routerModule.events.routerReplace
        });

        // successUpsellRegistrationCompleted → if first login and no next non-sale upsell, go home + start product onboarding
        // NEW FLOW
        sample({
            clock: [
                this.upsellModule.effects.registerEcommpayUpsellFx.doneData,
                this.upsellModule.effects.registerTruegateUpsellFx.doneData,
                this.upsellModule.events.upsellChargeSucceeded,
                this.upsellModule.events.upsellUpiPaymentSucceeded,
                this.upsellModule.effects.chargeAppleUpsellFx.doneData
            ],
            source: {
                upsells: this.upsellModule.stores.$upsells,
                currentUpsellId: this.upsellModule.stores.$currentUpsellId,
                fromProfile: this.upsellModule.stores.$fromProfile,
                firstLoginFlag: this.upsellModule.stores.$firstLoginFlag,
                isMetaBrowser: this.platformContextModule.stores.$isMetaBrowser
            },
            filter: (s) => {
                if (
                    s.fromProfile ||
                    !s.currentUpsellId ||
                    !s.firstLoginFlag ||
                    s.isMetaBrowser
                )
                    return false;
                const currentIndex = s.upsells.findIndex((u) => u.upsellId === s.currentUpsellId);
                if (currentIndex === -1) return true;
                const nextNonSaleIndex = s.upsells.findIndex(
                    (u, idx) => idx > currentIndex && !u.upsellId.includes("sale")
                );
                return nextNonSaleIndex === -1;
            },
            fn: () => returnToProfileOrHomePageByLocalStorageFlag(),
            target: [
                this.routerModule.events.routerReplace,
                this.productOnboardingModule.events.finishUpsellAndStartProductOnboarding,
                this.upsellModule.events.claudeFunnelMetadataAppendRequested,
                this.upsellModule.events.firstLoginFlagReset
            ]
        });

        sample({
            clock: [
                this.upsellModule.effects.registerEcommpayUpsellFx.doneData,
                this.upsellModule.effects.registerTruegateUpsellFx.doneData,
                this.upsellModule.events.upsellChargeSucceeded,
                this.upsellModule.events.upsellUpiPaymentSucceeded,
                this.upsellModule.effects.chargeAppleUpsellFx.doneData
            ],
            source: {
                upsells: this.upsellModule.stores.$upsells,
                currentUpsellId: this.upsellModule.stores.$currentUpsellId,
                fromProfile: this.upsellModule.stores.$fromProfile,
                firstLoginFlag: this.upsellModule.stores.$firstLoginFlag,
                isMetaBrowser: this.platformContextModule.stores.$isMetaBrowser
            },
            filter: (s) => {
                if (
                    s.fromProfile ||
                    !s.currentUpsellId ||
                    !s.firstLoginFlag ||
                    !s.isMetaBrowser
                )
                    return false;
                const currentIndex = s.upsells.findIndex((u) => u.upsellId === s.currentUpsellId);
                if (currentIndex === -1) return true;
                const nextNonSaleIndex = s.upsells.findIndex(
                    (u, idx) => idx > currentIndex && !u.upsellId.includes("sale")
                );
                return nextNonSaleIndex === -1;
            },
            fn: () => {
                localStorage.removeItem("first_login");
                return "/change-browser";
            },
            target: [
                this.routerModule.events.routerReplace,
                this.upsellModule.events.claudeFunnelMetadataAppendRequested,
                this.upsellModule.events.firstLoginFlagReset
            ]
        });

        // successUpsellRegistrationCompleted → if from profile, return to profile or home
        // NEW FLOW
        sample({
            clock: [
                this.upsellModule.effects.registerEcommpayUpsellFx.doneData,
                this.upsellModule.effects.registerTruegateUpsellFx.doneData,
                this.upsellModule.events.upsellChargeSucceeded,
                this.upsellModule.events.upsellUpiPaymentSucceeded,
                this.upsellModule.effects.chargeAppleUpsellFx.doneData
            ],
            source: {
                fromProfileFlag: this.upsellModule.stores.$fromProfile,
                currentUpsellId: this.upsellModule.stores.$currentUpsellId
            },
            filter: (s) =>
                Boolean(s.fromProfileFlag) &&
                !Boolean(s.currentUpsellId?.includes("ai_tools")),
            fn: () => returnToProfileOrHomePageByLocalStorageFlag(),
            target: [this.routerModule.events.routerReplace, deleteFromProfileFlagFx]
        });

        // ai_tools bought from profile → go to assistant (shows processing status), not back to profile
        // NEW FLOW
        sample({
            clock: [
                this.upsellModule.effects.registerEcommpayUpsellFx.doneData,
                this.upsellModule.effects.registerTruegateUpsellFx.doneData,
                this.upsellModule.events.upsellChargeSucceeded,
                this.upsellModule.events.upsellUpiPaymentSucceeded,
                this.upsellModule.effects.chargeAppleUpsellFx.doneData
            ],
            source: {
                fromProfileFlag: this.upsellModule.stores.$fromProfile,
                currentUpsellId: this.upsellModule.stores.$currentUpsellId
            },
            filter: (s) =>
                Boolean(s.fromProfileFlag) &&
                Boolean(s.currentUpsellId?.includes("ai_tools")),
            target: [packAccessRedirectTriggered, deleteFromProfileFlagFx]
        });

        sample({
            clock: [
                this.upsellModule.effects.registerEcommpayUpsellFx.failData,
                this.upsellModule.effects.registerTruegateUpsellFx.failData,
                this.upsellModule.effects.chargeUpsellFx.failData,
                this.upsellModule.effects.chargeAppleUpsellFx.failData
            ],
            target: failedUpsellModalOpened
        });

        sample({
            clock: this.upsellModule.events.upsellPurchaseLimitReached,
            target: failedUpsellModalOpened
        });
        sample({
            clock: this.upsellModule.events.upsell3dsChallengeFailed,
            target: failedUpsellModalOpened
        });

        // Declines that Solidgate settled asynchronously arrive as HTTP 200 with a
        // failed order status; treat them exactly like a failed charge request.
        sample({
            clock: this.upsellModule.events.upsellChargeDeclined,
            target: failedUpsellModalOpened
        });

        // UPI failures do not show the failure modal, so continue through the
        // same navigation flow immediately instead of waiting for it to close.
        sample({
            clock: this.upsellModule.events.upsellUpiPaymentFailed,
            target: failedUpsellModalClosed
        });

        // failedUpsellModalClosed → if NOT first login and NOT from profile, redirect to pack page
        sample({
            clock: failedUpsellModalClosed,
            source: {
                currentUpsellId: this.upsellModule.stores.$currentUpsellId,
                fromProfile: this.upsellModule.stores.$fromProfile,
                firstLoginFlag: this.upsellModule.stores.$firstLoginFlag
            },
            filter: (s) => !s.fromProfile && !s.firstLoginFlag && s.currentUpsellId !== null,
            target: packAccessRedirectTriggered
        });

        // failedUpsellModalClosed → if first login, failedAttemptCount <= 2 and not fromProfile, redirect to next non-sale
        sample({
            clock: failedUpsellModalClosed,
            source: {
                failedAttemptCount: $failedAttemptCount,
                fromProfile: this.upsellModule.stores.$fromProfile,
                upsells: this.upsellModule.stores.$upsells,
                currentUpsellId: this.upsellModule.stores.$currentUpsellId,
                firstLoginFlag: this.upsellModule.stores.$firstLoginFlag
            },
            filter: (s) =>
                s.firstLoginFlag && s.failedAttemptCount <= 2 && !s.fromProfile && s.currentUpsellId !== null,
            fn: (s) => {
                const currentIndex = s.upsells.findIndex((u) => u.upsellId === s.currentUpsellId);
                if (currentIndex === -1) return "/home";
                const nextNonSaleIndex = s.upsells.findIndex(
                    (u, idx) => idx > currentIndex && !u.upsellId.includes("sale")
                );
                if (nextNonSaleIndex === -1) return "/home";
                return `/upsell/${s.upsells[nextNonSaleIndex].upsellId}`;
            },
            target: this.routerModule.events.routerReplace
        });

        // failedUpsellModalClosed → if first login, failedAttemptCount <= 2, not fromProfile, and no next non-sale → home + onboarding
        sample({
            clock: failedUpsellModalClosed,
            source: {
                failedAttemptCount: $failedAttemptCount,
                fromProfile: this.upsellModule.stores.$fromProfile,
                upsells: this.upsellModule.stores.$upsells,
                currentUpsellId: this.upsellModule.stores.$currentUpsellId,
                firstLoginFlag: this.upsellModule.stores.$firstLoginFlag,
                isMetaBrowser: this.platformContextModule.stores.$isMetaBrowser
            },
            filter: (s) => {
                if (
                    !s.firstLoginFlag ||
                    s.failedAttemptCount > 2 ||
                    s.fromProfile ||
                    !s.currentUpsellId ||
                    s.isMetaBrowser
                )
                    return false;
                const currentIndex = s.upsells.findIndex((u) => u.upsellId === s.currentUpsellId);
                if (currentIndex === -1) return true;
                const nextNonSaleIndex = s.upsells.findIndex(
                    (u, idx) => idx > currentIndex && !u.upsellId.includes("sale")
                );
                return nextNonSaleIndex === -1;
            },
            fn: () => returnToProfileOrHomePageByLocalStorageFlag(),
            target: [
                this.routerModule.events.routerReplace,
                this.productOnboardingModule.events.finishUpsellAndStartProductOnboarding,
                this.upsellModule.events.claudeFunnelMetadataAppendRequested,
                this.upsellModule.events.firstLoginFlagReset
            ]
        });

        sample({
            clock: failedUpsellModalClosed,
            source: {
                failedAttemptCount: $failedAttemptCount,
                fromProfile: this.upsellModule.stores.$fromProfile,
                upsells: this.upsellModule.stores.$upsells,
                currentUpsellId: this.upsellModule.stores.$currentUpsellId,
                firstLoginFlag: this.upsellModule.stores.$firstLoginFlag,
                isMetaBrowser: this.platformContextModule.stores.$isMetaBrowser
            },
            filter: (s) => {
                if (
                    !s.firstLoginFlag ||
                    s.failedAttemptCount > 2 ||
                    s.fromProfile ||
                    !s.currentUpsellId ||
                    !s.isMetaBrowser
                )
                    return false;
                const currentIndex = s.upsells.findIndex((u) => u.upsellId === s.currentUpsellId);
                if (currentIndex === -1) return true;
                const nextNonSaleIndex = s.upsells.findIndex(
                    (u, idx) => idx > currentIndex && !u.upsellId.includes("sale")
                );
                return nextNonSaleIndex === -1;
            },
            fn: () => {
                localStorage.removeItem("first_login");
                return "/change-browser";
            },
            target: [
                this.routerModule.events.routerReplace,
                this.upsellModule.events.claudeFunnelMetadataAppendRequested,
                this.upsellModule.events.firstLoginFlagReset
            ]
        });

        // failedUpsellModalClosed → if from profile, return to profile or home
        sample({
            clock: failedUpsellModalClosed,
            source: {
                fromProfile: this.upsellModule.stores.$fromProfile
            },
            filter: (s) => Boolean(s.fromProfile),
            fn: () => returnToProfileOrHomePageByLocalStorageFlag(),
            target: [this.routerModule.events.routerReplace, deleteFromProfileFlagFx]
        });

        reset({
            clock: routePathNameChanged,
            target: [$failedAttemptCount, $isFailedUpsellModalOpen]
        });
    }
}
